/**
 * Геометрия кадра аватара — одна и та же математика для превью, жестов и сервера.
 *
 * <p>Сервер хранит кадр как {@code AvatarCrop{side, offsetX, offsetY}}: сторона квадрата в долях
 * короткой стороны снимка и смещение центра в тех же долях. Редактор же работает в экранных
 * координатах: «во сколько раз приближено» и «на сколько пикселей сдвинуто». Обе величины живут
 * здесь и переводятся друг в друга без потерь, поэтому предпросмотр совпадает с тем, что вернёт
 * сервер, а не «примерно похож».
 */

export type AvatarCropValues = { side: number; offsetX: number; offsetY: number };

/**
 * Состояние жеста: во сколько раз приближён снимок относительно «короткая сторона ровно
 * закрывает маску» и на сколько экранных пикселей он сдвинут от центра маски.
 */
export type AvatarViewport = { scale: number; tx: number; ty: number };

export const IDENTITY_AVATAR_VIEWPORT: AvatarViewport = { scale: 1, tx: 0, ty: 0 };
/** Нижняя граница `side`, которую принимает сервер (`AvatarCrop.validate`). */
export const AVATAR_MIN_CROP_SIDE = 0.12;

/**
 * Санитарная граница смещения — та же, что в `AvatarCrop.validate` на сервере.
 *
 * <p>Осмысленный предел смещения зависит от пропорций снимка: чтобы дотянуться до левого края
 * кадром `side`, центру нужно уехать на `(max(w,h) / min(w,h) - side) / 2` коротких сторон. Для
 * 16:9 при сильном приближении это уже 0.83, для панорамы — единицы. Настоящее ограничение даёт
 * `clampAvatarViewport` (кадр не должен вылезти за снимок), здесь — только защита от мусора.
 */
export const AVATAR_MAX_CROP_OFFSET = 64;

/**
 * Ниже этого размера кадра в пикселях исходника приближать нечего: аватар всё равно
 * разворачивается в 512 px, и дальнейший зум даёт не деталь, а кашу.
 */
export const AVATAR_MIN_CROP_PX = 128;

function clamp(v: number, lo: number, hi: number): number {
  if (!Number.isFinite(v)) return lo;
  const r = Math.max(lo, Math.min(hi, v));
  // Ноль со знаком минус — валидное число, но здесь он рождается из `-0 / n` и ломает и
  // сравнения, и мемоизацию по значению. Приводим к обычному нулю.
  return r === 0 ? 0 : r;
}

/**
 * Mirrors {@code UserAvatarService.applyAvatarCrop} (Java): square crop on the decoded bitmap.
 * Used so the in-app preview matches the server output before/after upload.
 */
export function computeServerAvatarCropRect(
  widthPx: number,
  heightPx: number,
  side: number,
  offsetX: number,
  offsetY: number,
): { x0: number; y0: number; sidePx: number } {
  const w = Math.max(1, Math.floor(widthPx));
  const h = Math.max(1, Math.floor(heightPx));
  const min = Math.min(w, h);
  let sidePx = Math.round(min * Math.min(1.0, side));
  sidePx = Math.max(2, Math.min(sidePx, min));
  const cx = Math.round(w * 0.5 + offsetX * min);
  const cy = Math.round(h * 0.5 + offsetY * min);
  const half = Math.trunc(sidePx / 2);
  const x0 = Math.max(0, Math.min(w - sidePx, cx - half));
  const y0 = Math.max(0, Math.min(h - sidePx, cy - half));
  return { x0, y0, sidePx };
}

/**
 * Предел приближения. Берём меньшее из двух: что примет сервер и что осталось от разрешения
 * снимка. Для маленькой картинки зум обрывается раньше — это честнее, чем позволить растянуть
 * 90 пикселей на весь аватар.
 */
export function maxAvatarScale(imageW: number, imageH: number): number {
  const min = Math.max(1, Math.min(Math.floor(imageW), Math.floor(imageH)));
  return Math.max(1, Math.min(1 / AVATAR_MIN_CROP_SIDE, min / AVATAR_MIN_CROP_PX));
}

/**
 * Приводит состояние жеста к допустимому: масштаб в разрешённых пределах, а сдвиг такой,
 * чтобы маска оставалась полностью закрытой снимком. Без этого палец уводит кадр за край,
 * сервер молча возвращает его обратно — и результат не совпадает с тем, что человек видел.
 */
export function clampAvatarViewport(
  vp: AvatarViewport,
  imageW: number,
  imageH: number,
  maskPx: number,
): AvatarViewport {
  const w = Math.max(1, imageW);
  const h = Math.max(1, imageH);
  const min = Math.min(w, h);
  const mask = Math.max(1, maskPx);
  const scale = clamp(vp.scale, 1, maxAvatarScale(w, h));
  /** Сторона кадра в пикселях исходника. */
  const cropPx = min / scale;
  /** Экранных пикселей на пиксель исходника. */
  const px = (mask / min) * scale;
  const maxTx = Math.max(0, (w - cropPx) / 2) * px;
  const maxTy = Math.max(0, (h - cropPx) / 2) * px;
  return { scale, tx: clamp(vp.tx, -maxTx, maxTx), ty: clamp(vp.ty, -maxTy, maxTy) };
}

/** Состояние жеста → то, что уходит на сервер. */
export function avatarCropFromViewport(
  vp: AvatarViewport,
  imageW: number,
  imageH: number,
  maskPx: number,
): AvatarCropValues {
  const c = clampAvatarViewport(vp, imageW, imageH, maskPx);
  const mask = Math.max(1, maskPx);
  return {
    side: clamp(1 / c.scale, AVATAR_MIN_CROP_SIDE, 1),
    offsetX: clamp(-c.tx / (mask * c.scale), -AVATAR_MAX_CROP_OFFSET, AVATAR_MAX_CROP_OFFSET),
    offsetY: clamp(-c.ty / (mask * c.scale), -AVATAR_MAX_CROP_OFFSET, AVATAR_MAX_CROP_OFFSET),
  };
}

/** Обратный перевод: сохранённый кадр → состояние жеста. */
export function viewportFromAvatarCrop(
  crop: AvatarCropValues,
  imageW: number,
  imageH: number,
  maskPx: number,
): AvatarViewport {
  const mask = Math.max(1, maskPx);
  const side = clamp(crop.side, AVATAR_MIN_CROP_SIDE, 1);
  const scale = 1 / side;
  return clampAvatarViewport(
    { scale, tx: -crop.offsetX * mask * scale, ty: -crop.offsetY * mask * scale },
    imageW,
    imageH,
    maskPx,
  );
}

/**
 * Стартовый кадр по найденному лицу: портрет занимает примерно половину маски, центр чуть выше
 * середины лица — так остаётся воздух надо лбом, а подбородок не упирается в край.
 */
export function avatarViewportForFace(
  face: { x: number; y: number; width: number; height: number },
  imageW: number,
  imageH: number,
  maskPx: number,
): AvatarViewport {
  const w = Math.max(1, imageW);
  const h = Math.max(1, imageH);
  const min = Math.min(w, h);
  const mask = Math.max(1, maskPx);
  if (!(face.width > 0) || !(face.height > 0)) return IDENTITY_AVATAR_VIEWPORT;
  const cropPx = clamp(Math.max(face.width, face.height) * 2.2, AVATAR_MIN_CROP_PX, min);
  const scale = min / cropPx;
  const cx = face.x + face.width / 2;
  const cy = face.y + face.height / 2 - face.height * 0.12;
  const px = (mask / min) * scale;
  return clampAvatarViewport(
    { scale, tx: (w / 2 - cx) * px, ty: (h / 2 - cy) * px },
    imageW,
    imageH,
    maskPx,
  );
}
